import React, { useState, useEffect, DragEvent, useRef } from 'react';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import { apiFetch, apiOpenBlob } from '../../lib/api';
import BACKEND_URL from '../../config.js';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './DocumentiSocietariPage.api';
import '../../styles/aziendale.css';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface UtenteMin { id: number; nome: string; }
interface DocSocietario {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  tipo: string;
  descrizione: string | null;
  nomeFile: string;
  uploadedAt: string;
  updatedAt: string;
  uploadedBy: UtenteMin;
}

// ── Component ─────────────────────────────────────────────────────────────
export default function DocumentiSocietariPage() {
  const [items, setItems] = useState<DocSocietario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const { isSuperadmin } = usePermesso();

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Upload form state
  const [uploadSocietaId, setUploadSocietaId] = useState<string>('');
  const [uploadTipo, setUploadTipo] = useState('');
  const [uploadDescrizione, setUploadDescrizione] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal modifica
  const [editModal, setEditModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editTipo, setEditTipo] = useState('');
  const [editDescrizione, setEditDescrizione] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Modal sostituzione file
  const [replaceModal, setReplaceModal] = useState(false);
  const [replaceId, setReplaceId] = useState<number | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceSaving, setReplaceSaving] = useState(false);
  const replaceFileRef = useRef<HTMLInputElement>(null);

  // Sync uploadSocietaId dalla topbar
  useEffect(() => {
    if (filterSocietaId) setUploadSocietaId(filterSocietaId);
  }, [filterSocietaId]);

  useEffect(() => { fetchItems(); }, [pronta, filterSocietaId]); // eslint-disable-line

  const fetchItems = async () => {
    if (!pronta) return;
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filterSocietaId) params.append('societaId', filterSocietaId);
      // @api GET /api/documenti-societari?societaId= -> {"success":true,"data":[...]}
      const res = await apiFetch<{ success: boolean; data: DocSocietario[] }>(`/api/documenti-societari?${params.toString()}`);
      setItems(res.data ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) {
      const f = e.dataTransfer.files[0];
      if (f.type === 'application/pdf') setUploadFile(f);
      else alert('Sono permessi solo file PDF');
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) { alert('Seleziona un file PDF'); return; }
    const sid = uploadSocietaId || filterSocietaId;
    if (!sid) { alert('Seleziona la Società'); return; }
    if (!uploadTipo.trim()) { alert('Inserisci il tipo di documento'); return; }

    try {
      setIsUploading(true);
      const fd = new FormData();
      fd.append('societaId', sid);
      fd.append('tipo', uploadTipo.trim());
      fd.append('descrizione', uploadDescrizione.trim());
      fd.append('file', uploadFile);

      const token = localStorage.getItem('token');
      // @api POST /api/documenti-societari FormData{societaId,tipo,descrizione,file} -> {"success":true,"id":"number"}
      const res = await fetch(`${BACKEND_URL}/api/documenti-societari`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Errore di caricamento');
      }

      setUploadFile(null);
      setUploadTipo('');
      setUploadDescrizione('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchItems();
      alert('Documento caricato con successo!');
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore durante l\'upload');
    } finally {
      setIsUploading(false);
    }
  };

  // ── Visualizza PDF ────────────────────────────────────────────────────
  const handleView = async (id: number, nomeFile: string) => {
    try {
      // L'endpoint ora restituisce sempre il file, mai un JSON con un link:
      // qui prima si gestivano entrambi i casi perche' il backend poteva
      // rispondere in due modi diversi a seconda che la firma riuscisse.
      // @api GET /api/documenti-societari/versioni/:id/view -> file stream (inline)
      await apiOpenBlob(`/api/documenti-societari/versioni/${id}/view`);
    } catch (err) {
      console.error('[handleView]', err);
      alert('Errore apertura PDF');
    }
  };

  // ── Modifica metadati ──────────────────────────────────────────────────
  const openEdit = (doc: DocSocietario) => {
    setEditId(doc.id);
    setEditTipo(doc.tipo);
    setEditDescrizione(doc.descrizione || '');
    setEditModal(true);
  };
  const closeEdit = () => { setEditModal(false); setEditId(null); };

  const saveEdit = async () => {
    if (!editId || !editTipo.trim()) return;
    setEditSaving(true);
    try {
      // @api PUT /api/documenti-societari/:id {"tipo":"string","descrizione":"string"} -> {"success":true}
      await apiFetch(`/api/documenti-societari/${editId}`, {
        method: 'PUT',
        body: JSON.stringify({ tipo: editTipo.trim(), descrizione: editDescrizione.trim() }),
      });
      closeEdit();
      fetchItems();
    } catch (err: any) {
      alert(err.message || 'Errore salvataggio');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Sostituzione file ──────────────────────────────────────────────────
  const openReplace = (doc: DocSocietario) => {
    setReplaceId(doc.id);
    setReplaceFile(null);
    setReplaceModal(true);
  };
  const closeReplace = () => { setReplaceModal(false); setReplaceId(null); setReplaceFile(null); };

  const handleReplaceDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); };
  const handleReplaceDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) {
      const f = e.dataTransfer.files[0];
      if (f.type === 'application/pdf') setReplaceFile(f);
      else alert('Sono permessi solo file PDF');
    }
  };

  const saveReplace = async () => {
    if (!replaceId || !replaceFile) return;
    setReplaceSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', replaceFile);
      const token = localStorage.getItem('token');
      // @api PUT /api/documenti-societari/:id/file FormData{file} -> {"success":true}
      const res = await fetch(`${BACKEND_URL}/api/documenti-societari/${replaceId}/file`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Errore sostituzione');
      }
      closeReplace();
      fetchItems();
      alert('File sostituito con successo!');
    } catch (err: any) {
      alert(err.message || 'Errore sostituzione file');
    } finally {
      setReplaceSaving(false);
    }
  };

  // ── Elimina ────────────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    if (!window.confirm('Eliminare questo documento definitivamente? L\'azione non è reversibile.')) return;
    try {
      // @api DELETE /api/documenti-societari/:id -> {"success":true}
      await apiFetch(`/api/documenti-societari/${id}`, { method: 'DELETE' });
      fetchItems();
    } catch (err: any) {
      alert(err.message || 'Errore eliminazione');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout title="Documenti Societari">

      {/* ── CARD UPLOAD ── */}
      <div className="bop-bilanci__upload-card">
        <h2 className="bop-bilanci__upload-title">Carica / Aggiorna Documento</h2>

        <form onSubmit={handleUpload} className="bop-bilanci__upload-form">
          <div className="bop-bilanci__form-col">
            {!isUnica && (
              <div className="bop-form-group">
                <label className="bop-label">Società *</label>
                <select
                  value={uploadSocietaId}
                  onChange={e => setUploadSocietaId(e.target.value)}
                  className="bop-select"
                  required
                >
                  <option value="">Seleziona società...</option>
                  {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}

            <div className="bop-form-group">
              <label className="bop-label">Tipo documento *</label>
              <input
                className="bop-input"
                value={uploadTipo}
                onChange={e => setUploadTipo(e.target.value)}
                placeholder="es. Atto Costitutivo, Statuto, Visura Camerale..."
                required
              />
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Descrizione</label>
              <textarea
                className="bop-bilanci__textarea"
                placeholder="Note sul documento..."
                value={uploadDescrizione}
                onChange={e => setUploadDescrizione(e.target.value)}
              />
            </div>
          </div>

          <div className="bop-bilanci__form-col">
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`bop-bilanci__dropzone${uploadFile ? ' bop-bilanci__dropzone--has-file' : ''}`}
            >
              <input
                type="file"
                accept="application/pdf"
                ref={fileInputRef}
                className="bop-bilanci__file-input-hidden"
                onChange={e => {
                  if (e.target.files?.length) setUploadFile(e.target.files[0]);
                }}
              />
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={uploadFile ? '#16a34a' : '#64748b'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bop-bilanci__dropzone-icon">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              {uploadFile ? (
                <div>
                  <div className="bop-bilanci__dropzone-selected-label">File selezionato:</div>
                  <div className="bop-bilanci__dropzone-selected-name">{uploadFile.name}</div>
                  <div className="bop-bilanci__dropzone-selected-hint">Clicca o trascina per cambiare</div>
                </div>
              ) : (
                <div className="bop-bilanci__dropzone-placeholder">
                  <span className="bop-bilanci__dropzone-link">Clicca per esplorare</span> oppure trascina e rilascia<br />
                  <span className="bop-bilanci__dropzone-hint">Solo file PDF, max 20 MB</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isUploading || !uploadFile}
              className="bop-bilanci__upload-btn"
            >
              {isUploading ? (
                <>
                  <span className="bop-bilanci__upload-spinner" />
                  Caricamento in corso...
                </>
              ) : 'Carica Documento'}
            </button>
          </div>
        </form>
      </div>

      {/* ── ARCHIVIO ── */}
      <div className="bop-bilanci__archive-card">
        <div className="bop-bilanci__archive-header">
          <h2 className="bop-bilanci__archive-title">Archivio Documenti Societari</h2>
          <div className="bop-bilanci__archive-filters">
            {!isUnica && (
              <select
                value={filterSocietaId}
                onChange={e => setFilterSocietaId(e.target.value)}
                className="bop-bilanci__filter-select"
              >
                <option value="">Tutte le società...</option>
                {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="bop-bilanci__loading">Caricamento archivio...</div>
        ) : items.length === 0 ? (
          <div className="bop-bilanci__empty">Nessun documento trovato.</div>
        ) : (
          <div className="aziendale-table-wrapper">
            <table className="aziendale-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  {!isUnica && <th>Società</th>}
                  <th>Descrizione</th>
                  <th>File</th>
                  <th>Caricato da</th>
                  <th>Data caricamento</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(doc => (
                  <tr key={doc.id}>
                    <td className="aziendale-table-cell-secondary">{doc.tipo}</td>
                    {!isUnica && <td className="aziendale-table-cell-muted">{doc.societa.nome}</td>}
                    <td className="aziendale-table-cell-muted">
                      {doc.descrizione
                        ? (doc.descrizione.length > 60
                          ? <span title={doc.descrizione}>{doc.descrizione.substring(0, 60)}...</span>
                          : doc.descrizione)
                        : <span className="aziendale-table-cell-em">—</span>}
                    </td>
                    <td className="aziendale-table-cell-secondary">{doc.nomeFile}</td>
                    <td className="aziendale-table-cell-muted">{doc.uploadedBy.nome}</td>
                    <td className="aziendale-table-cell-muted">{new Date(doc.uploadedAt).toLocaleDateString('it-IT')}</td>
                    <td>
                      <div className="aziendale-table-actions">
                        <button className="aziendale-btn-view" onClick={() => handleView(doc.id, doc.nomeFile)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          Visualizza
                        </button>
                        <button className="aziendale-btn-modifica" onClick={() => openEdit(doc)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Modifica
                        </button>
                        <button className="aziendale-btn-sostituisci" onClick={() => openReplace(doc)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
                          Sostituisci file
                        </button>
                        {isSuperadmin && (
                          <button className="aziendale-btn-delete" onClick={() => handleDelete(doc.id)}>
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Modifica ── */}
      {editModal && (
        <Modal title="Modifica documento" onClose={closeEdit} footer={
          <>
            <button className="bop-btn bop-btn-secondary" onClick={closeEdit}>Annulla</button>
            <button className="bop-btn bop-btn-primary" onClick={saveEdit} disabled={editSaving || !editTipo.trim()}>
              {editSaving ? 'Salvo...' : 'Salva'}
            </button>
          </>
        }>
          <div className="bop-form-group">
            <label className="bop-label">Tipo documento *</label>
            <input className="bop-input" value={editTipo} onChange={e => setEditTipo(e.target.value)} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione</label>
            <textarea className="bop-bilanci__textarea" value={editDescrizione} onChange={e => setEditDescrizione(e.target.value)} />
          </div>
        </Modal>
      )}

      {/* ── Modal Sostituisci File ── */}
      {replaceModal && (
        <Modal title="Sostituisci file" onClose={closeReplace} footer={
          <>
            <button className="bop-btn bop-btn-secondary" onClick={closeReplace}>Annulla</button>
            <button className="bop-btn bop-btn-primary" onClick={saveReplace} disabled={replaceSaving || !replaceFile}>
              {replaceSaving ? 'Sostituzione...' : 'Sostituisci'}
            </button>
          </>
        }>
          <div
            onDragOver={handleReplaceDragOver}
            onDrop={handleReplaceDrop}
            onClick={() => replaceFileRef.current?.click()}
            className={`bop-bilanci__dropzone${replaceFile ? ' bop-bilanci__dropzone--has-file' : ''}`}
          >
            <input
              type="file"
              accept="application/pdf"
              ref={replaceFileRef}
              className="bop-bilanci__file-input-hidden"
              onChange={e => {
                if (e.target.files?.length) setReplaceFile(e.target.files[0]);
              }}
            />
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={replaceFile ? '#16a34a' : '#64748b'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bop-bilanci__dropzone-icon">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            {replaceFile ? (
              <div>
                <div className="bop-bilanci__dropzone-selected-label">Nuovo file:</div>
                <div className="bop-bilanci__dropzone-selected-name">{replaceFile.name}</div>
                <div className="bop-bilanci__dropzone-selected-hint">Clicca o trascina per cambiare</div>
              </div>
            ) : (
              <div className="bop-bilanci__dropzone-placeholder">
                <span className="bop-bilanci__dropzone-link">Clicca per esplorare</span> oppure trascina e rilascia<br />
                <span className="bop-bilanci__dropzone-hint">Solo file PDF, max 20 MB</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
